import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Card, CardContent } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import DragIndicatorIcon from '@guildeducationinc/recess/icons/DragIndicatorIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { Chip } from '@mui/material';
import { TEST_POLICY_COVERAGE } from '@src/graphql/queries/rule';
import { RuleType, SchoolOption } from '@src/types';
import { cleanCriteria } from '@src/utilities/criteriaHelper';
import { Reorder, useDragControls } from 'framer-motion';
import _ from 'lodash';
import { CLIENT_CONFIG, Confirm, DELETE_RULE, FilterBuilder, REORDER_RULES, RuleTargetType, SAVE_RULE, TrackableButton, emptyRule, useLocale, useRules, } from 'pe-core-frontend';
import { createContext, useContext, useEffect, useState } from 'react';
import { Rule } from './Rule';
import { RuleExceptionStatusTag } from './RuleExceptionStatusTag';
import styles from './Rules.module.scss';
export const RulesContext = createContext({
    clientId: 0,
    ruleType: RuleType.ELIGIBILITY,
    title: '',
    targetPopulationEntityEnumType: {},
    defaultTargetPopulationEntityType: '',
    testPolicyCoverage: undefined,
    emptyPolicy: {
        ruleType: '',
    },
    children: () => _jsx(_Fragment, {}),
});
export const Rules = (props) => {
    const { t } = useLocale();
    const [rules, setRules] = useState([]);
    const [selectedRule, setSelectedRule] = useState();
    const [saveRule] = useMutation(SAVE_RULE);
    const [deleteRule] = useMutation(DELETE_RULE);
    const [reorderRules] = useMutation(REORDER_RULES);
    const [saving, setSaving] = useState(false);
    const [confirmDuplicateEveryone, setConfirmDuplicateEveryone] = useState(null);
    const [confirmMissingDefaultCountry, setConfirmMissingDefaultCountry] = useState(null);
    // Fetch the client config for the client being administered (not the current user's client)
    const { data: clientConfigData } = useQuery(CLIENT_CONFIG, {
        variables: { clientId: props.clientId },
    });
    const clientConfig = clientConfigData?.clientConfig;
    const { items, loading, refetch } = useRules({
        clientId: props.clientId,
        ruleType: props.ruleType,
        ruleSpec: props.ruleSpec,
    });
    useEffect(() => {
        setRules(items ?? []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);
    const handleAddRule = () => {
        setSelectedRule({
            ...emptyRule,
            id: 0,
            clientId: props.clientId,
            ruleType: props.ruleType,
            sortOrder: rules.length === 0 ? 1 : Math.max(...rules.map(r => r.sortOrder)) + 1,
            policy: {
                ...props.emptyPolicy,
                ruleType: props.ruleType,
            },
        });
    };
    const handleEditRule = (rule) => {
        setSelectedRule(rule);
    };
    const handleDeleteRule = async (rule) => {
        if (rule.id) {
            await deleteRule({
                variables: {
                    id: rule.id,
                },
            });
            refetch();
        }
    };
    const handleReorder = async (reorderedRules) => {
        // Sort to ensure exception rules are at the top (is_exception DESC, sort_order ASC)
        const sortedRules = [...reorderedRules].sort((a, b) => {
            // First sort by is_exception (exceptions first)
            if (a.isException !== b.isException) {
                return a.isException ? -1 : 1;
            }
            // Then maintain the order within each group
            return 0;
        });
        const ids = sortedRules.map(rule => rule.id);
        try {
            setSaving(true);
            await reorderRules({
                variables: {
                    ids,
                },
            });
            setRules(sortedRules);
            // refetch();
        }
        finally {
            setSaving(false);
        }
    };
    const handleSaveRule = async (skipDuplicateCheck = false, skipMissingCountryCheck = false) => {
        if (!selectedRule)
            return;
        setSaving(true);
        try {
            // Check for duplicate "everyone" rules and confirm before proceeding
            if (!skipDuplicateCheck && selectedRule.target === RuleTargetType.ALL) {
                const existingEveryoneRule = rules.find(rule => rule.target === RuleTargetType.ALL &&
                    rule.ruleType === selectedRule.ruleType &&
                    rule.id !== selectedRule.id);
                if (existingEveryoneRule) {
                    setConfirmDuplicateEveryone(selectedRule);
                    setSaving(false);
                    return;
                }
            }
            // Check if default client country is missing from school policy countries
            if (!skipMissingCountryCheck &&
                selectedRule.ruleType === RuleType.SCHOOL &&
                clientConfig?.country) {
                const schoolPolicy = selectedRule.policy;
                if (schoolPolicy.schoolOption === SchoolOption.SCHOOL &&
                    (!schoolPolicy.countries ||
                        schoolPolicy.countries.length === 0 ||
                        !schoolPolicy.countries.includes(clientConfig.country))) {
                    setConfirmMissingDefaultCountry(selectedRule);
                    setSaving(false);
                    return;
                }
            }
            // Clean criteria before saving - remove incomplete conditions
            // cleanCriteria returns proper "everyone" format for empty criteria
            const criteria = cleanCriteria(selectedRule.criteria);
            // save
            await saveRule({
                variables: {
                    input: {
                        ..._.omit(selectedRule, ['id', 'createdAt', 'updatedAt', '__typename', 'criteria']),
                        criteria,
                        id: selectedRule.id ? selectedRule.id : null,
                        sortOrder: Number(selectedRule.sortOrder),
                        policy: {
                            ...selectedRule.policy,
                            ruleType: props.ruleType,
                        },
                    },
                },
            });
            refetch();
            setSelectedRule(undefined);
        }
        finally {
            setSaving(false);
        }
    };
    const handleCancelRule = () => {
        setSelectedRule(undefined);
    };
    const [testPolicyResult, setTestPolicyResult] = useState(null);
    const [testCoverageQuery, testCoverageResult] = useLazyQuery(TEST_POLICY_COVERAGE);
    useEffect(() => {
        if (testCoverageResult?.data) {
            setTestPolicyResult({
                found: testCoverageResult.data.testPolicyCoverage.found,
                total: testCoverageResult.data.testPolicyCoverage.total,
            });
        }
        if (testCoverageResult?.error) {
            setTestPolicyResult({
                error: testCoverageResult.error.message,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testCoverageResult]);
    const handleTestPolicy = async () => {
        if (props.testPolicyCoverage) {
            await testCoverageQuery({
                variables: {
                    clientId: props.clientId,
                    ruleType: props.ruleType,
                    policyItemName: props.testPolicyCoverage.policyItemName,
                    policyItemValue: props.testPolicyCoverage.policyItemValue,
                },
            });
        }
    };
    return (_jsx(RulesContext.Provider, { value: props, children: _jsxs("div", { className: 'px-5', children: [selectedRule && (_jsx(Rule, { rule: selectedRule, onChange: setSelectedRule, onSave: handleSaveRule, onCancel: handleCancelRule })), !selectedRule && (_jsxs("div", { className: 'my-2', children: [_jsxs("div", { className: 'flex flexJustify--end gap-2 mb-2', children: [props.testPolicyCoverage && (_jsxs("div", { className: 'grow text-right', children: [testPolicyResult && (_jsxs("span", { className: 'font-bold mr-1', children: [!testPolicyResult.error && (_jsx("span", { children: t(props.testPolicyCoverage.message, {
                                                        values: {
                                                            found: testPolicyResult.found,
                                                            total: testPolicyResult.total,
                                                        },
                                                    }) })), testPolicyResult.error && (_jsx("span", { className: 'text-red-600', children: t('Error running test') }))] })), _jsx(TrackableButton, { btnType: 'mui', enText: 'Test Coverage', tag: props.ruleType, variant: 'outlined', type: 'button', onClick: () => handleTestPolicy(), children: t('Test Coverage') })] })), _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Rule', tag: props.ruleType, variant: 'contained', type: 'button', onClick: () => handleAddRule(), children: t('Add Rule') })] }), !loading && (_jsx("div", { children: _jsxs(Reorder.Group, { className: styles.rulesList, as: 'div', axis: 'y', values: rules, onReorder: handleReorder, children: [!rules || (rules.length === 0 && _jsx("div", { children: t('No policy is configured') })), rules.map(rule => (_jsx(RuleRow, { rule: rule, props: props, handleEditRule: handleEditRule, handleDeleteRule: handleDeleteRule, saving: saving }, rule.id)))] }) }))] })), confirmDuplicateEveryone && (_jsx(Confirm, { message: 'An "Everyone" rule already exists for this rule type. Are you sure you want to create another one?', onConfirm: () => {
                        handleSaveRule(true);
                        setConfirmDuplicateEveryone(null);
                    }, onCancel: () => setConfirmDuplicateEveryone(null) })), confirmMissingDefaultCountry && (_jsx(Confirm, { message: `The default client country (${clientConfig?.country}) is not included in this School's Policy Countries list. Are you sure you want to save this rule?`, onConfirm: () => {
                        handleSaveRule(false, true);
                        setConfirmMissingDefaultCountry(null);
                    }, onCancel: () => setConfirmMissingDefaultCountry(null) }))] }) }));
};
const RuleRow = ({ rule, props, handleEditRule, handleDeleteRule, saving }) => {
    const { t } = useLocale();
    const controls = useDragControls();
    const rulesProps = useContext(RulesContext);
    return (_jsx(Reorder.Item, { as: 'div', value: rule, dragListener: false, dragControls: controls, children: _jsx(Card, { children: _jsxs(CardContent, { className: styles.ruleCard, children: [_jsx("div", { className: styles.reorderHandle, onPointerDown: e => !saving && controls.start(e), children: _jsx(DragIndicatorIcon, { color: 'gray', size: 24 }) }), _jsxs("div", { className: styles.ruleContent, children: [_jsx("div", { className: styles.policy, children: props.children({
                                    clientId: rule.clientId,
                                    policy: rule.policy,
                                    onChange: () => { },
                                    readonly: true,
                                }) }), _jsxs("div", { className: styles.target, children: [rule.isException && (_jsx(RuleExceptionStatusTag, { startsOn: rule.startsOn, expiresOn: rule.expiresOn, comment: rule.comment })), _jsxs("div", { className: 'flex flexItems--center', children: [t('Applies To'), ":\u00A0\u00A0", rule.target === RuleTargetType.ALL && (_jsx(Chip, { variant: 'outlined', label: t('Everyone') }))] }), rule.target !== RuleTargetType.ALL && (
                                    // <FilterView clientId={rule.clientId} criteria={rule.criteria} />
                                    _jsx(FilterBuilder, { clientId: rule.clientId, criteria: rule.criteria, entityEnumType: rulesProps.targetPopulationEntityEnumType, hideName: true, initialCondition: true, defaultEntityType: rulesProps.defaultTargetPopulationEntityType, readonly: true, customizedMessaging: true }))] })] }), _jsxs("div", { className: styles.actions, children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `rule_${rule.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), className: 'margin--right_8', onClick: () => handleEditRule(rule) }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `rule_${rule.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => handleDeleteRule(rule) })] })] }) }) }, rule.id));
};
